import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FilmSlate, MagnifyingGlass, House, Sparkle, ArrowLeft, Buildings, Images,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import SocialStudioTab from "@/components/SocialStudioTab";
import SocialStudioTabV2 from "@/components/SocialStudioTabV2";
import { authedFileUrl } from "@/components/propertyDetail/helpers";

/**
 * SocialStudioPage — accesso diretto al Social Studio dalla sidebar.
 *
 * UX:
 *   1. Lista immobili (con foto e prezzo) → l'agente sceglie quello su cui
 *      lavorare adesso.
 *   2. Click su un immobile → apre `SocialStudioTab` come overlay. La logica
 *      è quella già esistente nel PropertyDetail; qui semplicemente non c'è
 *      tutto il chrome del dettaglio (foto, contatti, ecc.).
 *   3. "Indietro" torna alla lista.
 *
 * Niente duplicazione di logica: riusiamo il SocialStudioTab che esiste già.
 */
export default function SocialStudio() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPid = searchParams.get("pid");
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedPid, setSelectedPid] = useState(initialPid || null);

  useEffect(() => {
    // Se arriviamo con ?pid=... (es. dal flusso Galleria "Crea contenuti"),
    // resta in modalità studio su quella property — anche se non appare in
    // lista (è ad-hoc nascosta).
    if (initialPid) setSelectedPid(initialPid);
  }, [initialPid]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/properties");
        setProps(Array.isArray(data) ? data : []);
      } catch {
        toast.error("Errore caricamento immobili");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return props;
    return props.filter((p) =>
      [p.title, p.city, p.address, p.zone].some((v) => (v || "").toLowerCase().includes(s)),
    );
  }, [props, q]);

  if (selectedPid) {
    // iter 313 r21 v20 — user feedback: "fammi comunque la versione completa,
    // non semplificata". Forzato V1 (SocialStudioTab) sempre. Mantengo il
    // query param `?v2=1` come escape hatch nascosto per regression test.
    const v2Param = searchParams.get("v2");
    const useV2 = v2Param === "1"; // default OFF
    return (
      <AppShell>
        <div className="max-w-[1100px] mx-auto px-4 py-6">
          <button
            onClick={() => {
              setSelectedPid(null);
              // Pulisce il ?pid dalla URL così se l'utente fa back-forward
              // non rientra ancora nella stessa property.
              if (searchParams.get("pid")) {
                searchParams.delete("pid");
                setSearchParams(searchParams);
              }
            }}
            className="inline-flex items-center gap-1.5 text-[12px] text-[#6B6A65] hover:text-[#1C1C1A] mb-4 transition-colors"
            data-testid="social-studio-back"
          >
            <ArrowLeft size={13} weight="bold" />
            Torna all'Editor social
          </button>
          {useV2 ? (
            <SocialStudioTabV2 propertyId={selectedPid} />
          ) : (
            <SocialStudioTab propertyId={selectedPid} />
          )}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-[1100px] mx-auto px-4 py-6" data-testid="social-studio-page">
        {/* Header */}
        <div className="mb-5">
          <div className="inline-flex items-center gap-2 mb-2">
            <FilmSlate size={20} weight="fill" className="text-[#7C8B6E]" />
            <h1 className="font-editorial text-[26px] sm:text-[32px] text-[#1C1C1A] leading-tight">Editor social</h1>
          </div>
          <p className="text-[13.5px] text-[#6B6A65] max-w-[60ch]">
            PDF stampabile social, post Instagram, story 9:16, reel cinematici, video prima/dopo.
            Scegli un immobile per iniziare.
          </p>
          {/* iter v28 r240 — User M1157: «metti più chiaro che posso partire
              anche da una fotografia della galleria». Trasformato il piccolo
              link sottolineato in una CTA visibile a icona con sfondo. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Link
              to="/dashboard/galleria"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium text-[#1C1C1A] bg-[#F1F4ED] border border-[#C8CDB8] hover:bg-[#E2E8DA] hover:border-[#7C8B6E] rounded-full transition-colors"
              data-testid="social-studio-from-gallery"
            >
              <Images size={14} weight="bold" /> Parti da una foto della Galleria
            </Link>
            <span className="text-[11.5px] text-[#9A968B]">— senza dover scegliere un annuncio</span>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4 relative max-w-[420px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9A968B]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca immobile…"
            className="w-full pl-9 pr-3 py-2 text-[13.5px] border border-[#E8E5DE] rounded-md focus:outline-none focus:border-[#7C8B6E]"
            data-testid="social-studio-search"
          />
        </div>

        {/* Lista immobili */}
        {loading ? (
          <div className="py-10 text-center text-[#9A968B] text-[13px]">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <EmptyState q={q} hasNone={props.length === 0} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="social-studio-grid">
            {filtered.map((p) => (
              <PropertyCard
                key={p.id}
                property={p}
                onSelect={() => setSelectedPid(p.id)}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function PropertyCard({ property, onSelect }) {
  // iter 275: l'endpoint /properties popola solo cover_path (path interno).
  // Costruiamo l'URL autenticato come fa Dashboard.jsx, altrimenti la cover
  // ricadeva sul fallback House e non si vedeva mai la foto.
  // iter 306: thumbnail 480px lato server (cache) invece dell'originale 5+MB
  // — su mobile la lista carica in <1s anche con 20 immobili.
  const cover = authedFileUrl(property.cover_path, false, 480) || property.cover_url || property.photos?.[0]?.url;
  const price = property.price
    ? new Intl.NumberFormat("it-IT").format(property.price) + " €"
    : "—";
  const photoCount = property.photos_count ?? property.photo_count ?? property.photos?.length ?? 0;
  return (
    <button
      onClick={onSelect}
      className="text-left bg-white border border-[#E8E5DE] hover:border-[#7C8B6E] rounded-lg overflow-hidden transition-all hover:shadow-md group"
      data-testid={`social-studio-card-${property.id}`}
    >
      <div className="aspect-[4/3] bg-[#F1F4ED] relative overflow-hidden">
        {cover ? (
          <img
            src={cover}
            alt={property.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <House size={32} weight="thin" className="text-[#C8C5BD]" />
          </div>
        )}
        <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm text-[#1C1C1A] text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-1 rounded shadow-sm">
          <Sparkle size={10} weight="fill" className="text-[#7C8B6E]" /> Social
        </div>
      </div>
      <div className="p-3">
        <div className="text-[13.5px] font-semibold text-[#1C1C1A] leading-tight truncate" title={property.title}>
          {property.title || "(senza titolo)"}
        </div>
        <div className="mt-0.5 text-[11.5px] text-[#9A968B] truncate">
          {[property.city, property.zone].filter(Boolean).join(" · ") || "—"}
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[11.5px]">
          <span className="text-[#7C8B6E] font-semibold">{price}</span>
          <span className="text-[#9A968B]">
            {photoCount} foto
          </span>
        </div>
      </div>
    </button>
  );
}

function EmptyState({ q, hasNone }) {
  if (hasNone) {
    return (
      <div className="py-16 text-center bg-[#FAFAF7] border border-dashed border-[#E8E5DE] rounded-lg">
        <Buildings size={28} weight="thin" className="text-[#C8C5BD] mx-auto mb-2" />
        <div className="text-[13.5px] text-[#1C1C1A] font-medium">Nessun immobile ancora</div>
        <div className="text-[11.5px] text-[#9A968B] mt-1 mb-3">Crea un annuncio · oppure parti subito dalle foto che hai già</div>
        <div className="flex items-center justify-center gap-2">
          <Link
            to="/dashboard/annunci/nuovo"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[12px] font-medium rounded transition-colors"
          >
            Crea annuncio
          </Link>
          <Link
            to="/dashboard/galleria"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#F1F4ED] text-[12px] font-medium rounded transition-colors"
          >
            <Images size={11} weight="bold" /> Parti da foto
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="py-12 text-center text-[#9A968B] text-[13px]">
      Nessun immobile corrisponde a "{q}"
    </div>
  );
}
